import styles from '../tabs/Tab.module.scss';
import ArrowIcon from '@assets/icons/arrow-black-11.svg';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import cn from 'classnames';
import CommentIcon from '@assets/icons/comment-grey-18.svg';
import CopyTextToClipboard from '@components/CopyTextToClipboard';
import PencilIcon from '@assets/icons/pencil-grey-18.svg';
import TrashIcon from '@assets/icons/trash-grey-18.svg';
import { formatDate } from '@utils/formatValues';
import { Seller } from '@api/apiClient';
import { TColumnListProps } from '../types';
import { Text, TextColor, TextSize, TextWeight } from '@components/Text';
import {
  TEditMatchingExternalParcel,
  TInProcessParcelExternal,
  TInProcessParcelItem,
  TInProcessParcelItemAttachment
} from '@api/apiClient/dropshipping';
import { Tooltip } from '@components/new/Tooltip';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const defaultQuantity1 = 3;
const defaultQuantity2 = 4;

const ColumnList: React.FC<TColumnListProps> = (props: TColumnListProps): React.ReactElement => {
  const {
    calledFrom,
    data: {
      createdAt,
      externalParcels,
      externalMatchingId,
      id,
      message,
      parcels,
      status,
      trackNumber
    },
    handlerSetIsOpenDropshippingWindow,
    handlerSetIsOpenInfoWindow,
    handlerSetLinkedItems,
    handlerSetLinkedItemsComment,
    handlerSetLinkedItemsMatchingId,
    handlerSetLinkedItemsTrackNumbers
  } = props;

  const { t } = useTranslation();

  const [numberOfVisibleItems1, setNumberOfVisibleItems1] = useState<number>(defaultQuantity1);
  const [numberOfVisibleItems2, setNumberOfVisibleItems2] = useState<number>(defaultQuantity2);

  const condition: boolean = parcels?.length === numberOfVisibleItems1 && externalParcels?.length === numberOfVisibleItems2;
  const externalIds: string[] = parcels.map((item: TInProcessParcelItem) => item.externalId);
  const firstItem: TInProcessParcelItemAttachment = parcels[0]?.items?.[0];
  const sellers: Seller[] = parcels.map((item: TInProcessParcelItem) => item.seller).filter(v => v) as Seller[];
  const trackNumbers: TEditMatchingExternalParcel[] = externalParcels.map((item: TInProcessParcelExternal) => ({
    externalMarketplaceTrackNumber: item.externalMarketplaceTrackNumber,
    id: item.id
  }));
  const uniqueSellers: Seller[] = Array.from(new Map(sellers.map((seller: Seller) => [seller.id, seller])).values());

  return (

      <div className={styles.columnList}>
          <div className={styles.matchingTrackNumber}>
              <CopyTextToClipboard
                  key={trackNumber}
                  text={trackNumber}
                  visibleOnHover
                  tooltipPosition="right"
              >
                  <Text
                      color={TextColor.PRIMARY}
                      size={TextSize.S}
                      weight={TextWeight.BOLD}
                  >
                      {trackNumber}
                  </Text>
              </CopyTextToClipboard>
          </div>
        {firstItem ? (
            <>
              <span className={styles.imageWrapper}>
                  {firstItem.image && (
                      <img
                          className={styles.productImage}
                          src={firstItem.image}
                          alt="Image"
                      />
                  )}
              </span>
              <span className={styles.link}>
                <a
                    href={firstItem.url ?? ''}
                    target="_blank"
                    rel="noreferrer"
                >
                  {firstItem.name ?? ''}
                </a>
              </span>
            </>
        ) : (
            <>
              <span className={styles.imageWrapper}/>
              <span className={styles.link}/>
            </>
        )}
        <div className={styles.externalNumbers}>
          {parcels.map((item: TInProcessParcelItem, index: number) => {
            if (parcels.length === numberOfVisibleItems1 || index < numberOfVisibleItems1) {
              return (
                  <CopyTextToClipboard
                      key={item.externalId}
                      text={item.externalId}
                      visibleOnHover
                  >
                    <Text
                        color={TextColor.PRIMARY}
                        size={TextSize.S}
                        weight={TextWeight.BOLD}
                    >
                      {item.externalId}
                    </Text>
                  </CopyTextToClipboard>
              );
            }
          })}
        </div>
        <div className={styles.otherMarketplaceNumber}>
          <div className={styles.otherNumbers}>
            {externalParcels.map((item: TInProcessParcelExternal, index: number) => {
              if (externalParcels.length === numberOfVisibleItems2 || index < numberOfVisibleItems2) {
                return (
                    <Text
                        color={TextColor.DARK_MUTED}
                        key={item.externalMarketplaceTrackNumber}
                        size={TextSize.S}
                        ellipsis
                        lines={1}
                    >
                      {item.externalMarketplaceTrackNumber}
                    </Text>
                );
              }
            })}
          </div>
          {(parcels.length > defaultQuantity1 || externalParcels.length > defaultQuantity2) && (
              <div className={styles.wrapper}>
                <button
                    className={cn(styles.otherNumbersAction, condition ? styles.otherNumbersActionOpen : styles.otherNumbersActionClosed)}
                    onClick={() => {
                      setNumberOfVisibleItems1(condition ? defaultQuantity1 : parcels.length);
                      setNumberOfVisibleItems2(condition ? defaultQuantity2 : externalParcels.length);
                    }}
                >
                  <img
                      alt="Arrow"
                      src={ArrowIcon}
                  />
                </button>
              </div>
          )}
        </div>
        <div className={styles.wrapper}>
          <div className={styles.comment}>
            <Tooltip
                action={
                  <img
                      alt="Comment"
                      src={CommentIcon}
                  />
                }
                position="top"
                text={message}
                whiteSpace="pre-wrap"
            />
          </div>
        </div>
        <div className={cn('', condition ? styles.wrapperNone : styles.wrapper)}>
          <div className={styles.store}>
            {uniqueSellers.length === 1 ? (
                <AvatarWithMarketplace
                    id={uniqueSellers[0].id}
                    marketplace="Ozon"
                    name={uniqueSellers[0].name}
                    noHover
                    type="small"
                />
            ) : (
                <AvatarWithMarketplace
                    marketplace="Ozon"
                    name={uniqueSellers}
                    noHover
                    showAll={condition}
                    type="small"
                />
            )}
          </div>
        </div>
        <div className={cn(styles.wrapper, styles.wrapperColumn)}>
          <Text
            color={TextColor.PRIMARY}
            size={TextSize.S}
          >
            {formatDate(createdAt, 'dd.MM.yy')}
          </Text>
          <Text
            color={TextColor.DISABLED}
            size={TextSize.S}
          >
            {formatDate(createdAt, 'HH:mm')}
          </Text>
        </div>
        {calledFrom === 'inProcess' && (
            status === "Created" ? (
              <div className={cn(styles.wrapper)}>
                <Text
                  color={TextColor.PRIMARY}
                  size={TextSize.S}
                >
                  {t('dropshipping.inProcessTab.inProcessStatus')}
                </Text>
              </div>
            ) : (
              <div className={styles.actions}>
                <button
                    className={styles.action}
                    onClick={() => {
                      handlerSetIsOpenDropshippingWindow!(true);
                      handlerSetLinkedItems!(externalIds);
                      handlerSetLinkedItemsComment!(message);
                      handlerSetLinkedItemsMatchingId!(id);
                      handlerSetLinkedItemsTrackNumbers!(trackNumbers);
                    }}
                >
                  <img
                      src={PencilIcon}
                      alt="Pencil"
                  />
                </button>
                <button
                    className={styles.action}
                    onClick={() => {
                      handlerSetLinkedItemsMatchingId!(id);
                      handlerSetIsOpenInfoWindow!(externalMatchingId || "");
                    }}
                >
                  <img
                      src={TrashIcon}
                      alt="Trash"
                  />
                </button>
              </div>
            )
        )}
      </div>
  );
};

export default ColumnList;
